import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../../utils/tokenManager";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 6 * 60 * 1000;
const MAX_URLS = 100;

/**
 * Extracted from admin/ContactSearch.jsx - same 3-stage flow
 * (form -> progress -> results), same polling mechanism, same push/
 * discard endpoints, byte-for-byte.
 */
export function useContactSearch() {
  const { t } = useTranslation();

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [urlsText, setUrlsText] = useState("");
  const [formError, setFormError] = useState("");

  const [stage, setStage] = useState("form");
  const [job, setJob] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pushMessage, setPushMessage] = useState("");
  const [discardMessage, setDiscardMessage] = useState("");
  const [progressError, setProgressError] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [pushSucceeded, setPushSucceeded] = useState(false);

  const pollTimer = useRef(null);
  const pollStartedAt = useRef(null);
  const pushInFlightRef = useRef(false);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const accessToken = await getValidToken();
        const response = await axios({
          method: "get",
          url: `${process.env.REACT_APP_APIBASEURL}/api/clients`,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setClients(response.data.clients.map((client) => ({ label: `${client.Name} (ID: ${client.Id})`, value: client.Id })));
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  const urlCount = urlsText.split("\n").filter((l) => l.trim().length > 0).length;

  const pollJob = useCallback((jobId) => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollStartedAt.current = Date.now();
    let consecutiveErrors = 0;

    pollTimer.current = setInterval(async () => {
      if (Date.now() - pollStartedAt.current > MAX_POLL_DURATION_MS) {
        clearInterval(pollTimer.current);
        setProgressError(t("admin.contactSearch.errorTimeout"));
        return;
      }

      try {
        const accessToken = await getValidToken();
        const response = await axios({
          method: "get",
          url: `${process.env.REACT_APP_APIBASEURL}/api/admin/scrape-jobs/${jobId}`,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        consecutiveErrors = 0;
        setJob(response.data);

        if (response.data.status === "completed") {
          clearInterval(pollTimer.current);
          const successIds = response.data.results.filter((r) => r.is_successful).map((r) => r.id);
          setSelectedIds(new Set(successIds));
          setStage("results");
        } else if (response.data.status === "failed") {
          clearInterval(pollTimer.current);
          setProgressError(t("admin.contactSearch.errorJobFailed"));
        }
      } catch (error) {
        consecutiveErrors += 1;
        console.error("Error polling scrape job:", error);
        if (consecutiveErrors >= 3) {
          clearInterval(pollTimer.current);
          setProgressError(t("admin.contactSearch.errorTimeout"));
        }
      }
    }, POLL_INTERVAL_MS);
  }, [t]);

  const handleStart = async () => {
    const urls = urlsText.split("\n").map((u) => u.trim()).filter((u) => u.length > 0);

    if (!clientId) {
      setFormError(t("admin.contactSearch.errorNoClient"));
      return;
    }
    if (urls.length === 0) {
      setFormError(t("admin.contactSearch.errorNoUrls"));
      return;
    }
    setFormError("");
    setIsStarting(true);

    try {
      const accessToken = await getValidToken();
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_APIBASEURL}/api/admin/scrape-jobs`,
        data: { ClientId: Number(clientId), Urls: urls.slice(0, MAX_URLS) },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setJob({ job_id: response.data.job_id, status: "running", total: response.data.total, processed: 0, results: [] });
      setProgressError("");
      setStage("progress");
      pollJob(response.data.job_id);
    } catch (error) {
      console.error("Error starting scrape job:", error);
      setFormError(t("admin.contactSearch.errorStart"));
    } finally {
      setIsStarting(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePush = async () => {
    if (pushInFlightRef.current) return;
    pushInFlightRef.current = true;
    setIsPushing(true);
    setPushMessage("");
    try {
      const accessToken = await getValidToken();
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_APIBASEURL}/api/admin/scrape-jobs/${job.job_id}/push`,
        data: { result_ids: Array.from(selectedIds) },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const ids = (response.data.pushed_items || []).map((c) => c.id);
      let message = t("admin.contactSearch.pushSuccess", { count: response.data.pushed_count });
      if (ids.length > 0) message += ` ${t("admin.contactSearch.pushedIds")}: ${ids.join(", ")}.`;
      if (response.data.failed_count > 0) message += ` ${t("admin.contactSearch.pushPartialFailed", { count: response.data.failed_count })}`;
      setPushMessage(message);
      setPushSucceeded(true);
    } catch (error) {
      console.error("Error pushing scrape results:", error);
      setPushMessage(t("admin.contactSearch.errorPush"));
      setPushSucceeded(false);
    } finally {
      setIsPushing(false);
      pushInFlightRef.current = false;
    }
  };

  const handleDiscard = async () => {
    setIsDiscarding(true);
    try {
      const accessToken = await getValidToken();
      await axios({
        method: "delete",
        url: `${process.env.REACT_APP_APIBASEURL}/api/admin/scrape-jobs/${job.job_id}`,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch (error) {
      console.error("Error discarding scrape job:", error);
    } finally {
      setDiscardMessage(t("admin.contactSearch.discardSuccess"));
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  const successResults = job?.results?.filter((r) => r.is_successful) || [];
  const failedResults = job?.results?.filter((r) => !r.is_successful) || [];
  const progressPct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;

  return {
    clients,
    clientId,
    setClientId,
    urlsText,
    setUrlsText,
    formError,
    stage,
    job,
    selectedIds,
    toggleSelected,
    pushMessage,
    discardMessage,
    progressError,
    isStarting,
    isPushing,
    isDiscarding,
    pushSucceeded,
    urlCount,
    handleStart,
    handlePush,
    handleDiscard,
    successResults,
    failedResults,
    progressPct,
    MAX_URLS,
  };
}